import type { BarSeriesOption, PictorialBarSeriesOption } from 'echarts';

import {
  PictorialIcon,
  getPictorialIcon,
} from 'components/Studio/pictorialIcons';

// A Pictorial Bar's bars: its metrics' bar series drawn as icons (see
// Studio.PictorialOptions), each series in its own slot as a bar chart's
// are, with its metric's own icon or the chart's. Everything else (lines, labels, the drill-down's greying) stays as
// the bar chart draws it.

// Past this many, repeated icons would only overlap
const MAX_REPEATS = 60;
// Icons read better smaller than a wide bar would draw them
const MAX_ICON_WIDTH = 56;

// The category axis the background icons sit on: a hidden copy of the
// chart's, so they're laid out in the same slots as the bars they're behind
// without taking slots of their own
export const TRACK_CATEGORY_AXIS_INDEX = 1;

// What an icon's repeats (or one stretched icon) span: from the chart's
// lowest value to its highest on the series' axis, or the axis's own range.
// On repeat by a unit, each end rounds out to a whole number of icons.
const toBounds = ({
  model,
  axisIndex,
  unit,
}: {
  model: StudioCharts.CartesianModel;
  axisIndex: number;
  unit?: number;
}): { bounds: [number, number]; repeats?: number } => {
  const values = model.series
    .filter((s) => s.kind === 'bar' && s.axisIndex === axisIndex)
    .flatMap((s) => s.values)
    .filter((v): v is number => v !== null);
  const axis = model.valueAxes[axisIndex] ?? {};
  const low = Math.min(0, axis.min ?? Math.min(0, ...values));
  const high = Math.max(0, axis.max ?? Math.max(0, ...values));
  if (!unit || unit <= 0) {
    return { bounds: [low, high === low ? 1 : high] };
  }
  const repeats = Math.min(
    MAX_REPEATS,
    Math.max(1, Math.ceil(Math.max(Math.abs(low), high) / unit)),
  );
  return {
    bounds: [low < 0 ? -repeats * unit : 0, high > 0 ? repeats * unit : 0],
    repeats,
  };
};

// The icon a series' bars are drawn with: its own (keyed by its data key, so
// two series of one metric, each asking its own question, can differ), its
// metric's, or the chart's
export const getSeriesIcon = ({
  pictorial,
  series,
}: {
  pictorial: Studio.PictorialOptions;
  series: Pick<StudioCharts.Series, 'metricId' | 'seriesKey'>;
}): PictorialIcon => {
  const icons = pictorial.metricIcons ?? {};
  return getPictorialIcon(
    (series.seriesKey ? icons[series.seriesKey] : undefined) ??
      icons[series.metricId] ??
      pictorial.icon,
  );
};

// A path:// symbol for an icon, as ECharts draws it
export const toIconSymbol = (icon: PictorialIcon) => `path://${icon.path}`;

// Each icon as wide as its bar (as tall, sideways), the other way as its
// own shape has it. Stretched along the bar when it fills it.
const toSymbol = ({
  pictorial,
  icon,
  isHorizontal,
}: {
  pictorial: Studio.PictorialOptions;
  icon: PictorialIcon;
  isHorizontal: boolean;
}): Pick<
  PictorialBarSeriesOption,
  | 'symbol'
  | 'symbolSize'
  | 'symbolRepeat'
  | 'symbolClip'
  | 'symbolMargin'
  | 'symbolPosition'
> => {
  const symbol = toIconSymbol(icon);
  if (pictorial.mode === 'fill') {
    return {
      symbol,
      symbolSize: ['100%', '100%'],
      symbolRepeat: false,
      symbolClip: true,
      symbolPosition: 'start',
    };
  }
  // Repeated icons are sized from the bar's width both ways
  const along = isHorizontal
    ? (100 * icon.width) / icon.height
    : (100 * icon.height) / icon.width;
  return {
    symbol,
    symbolSize: isHorizontal ? [`${along}%`, '100%'] : ['100%', `${along}%`],
    symbolRepeat: 'fixed',
    symbolClip: true,
    symbolMargin: '12%',
  };
};

const SLOTS = {
  barGap: '20%',
  barCategoryGap: '30%',
  barMaxWidth: MAX_ICON_WIDTH,
};

// A metric's bars (as toBarSeriesOption draws them) drawn with the icon,
// up to each value, the last icon cut short
export const toPictorialSeriesOption = ({
  bar,
  series,
  model,
  pictorial,
}: {
  bar: BarSeriesOption;
  series: StudioCharts.Series;
  model: StudioCharts.CartesianModel;
  pictorial: Studio.PictorialOptions;
}): PictorialBarSeriesOption => {
  const { bounds, repeats } = toBounds({
    model,
    axisIndex: series.axisIndex,
    unit: pictorial.mode === 'fill' ? undefined : pictorial.unit,
  });
  const symbol = toSymbol({
    pictorial,
    icon: getSeriesIcon({ pictorial, series }),
    isHorizontal: model.isHorizontal,
  });
  return {
    type: 'pictorialBar',
    id: bar.id,
    name: bar.name,
    xAxisIndex: bar.xAxisIndex,
    yAxisIndex: bar.yAxisIndex,
    data: bar.data as PictorialBarSeriesOption['data'],
    label: bar.label as PictorialBarSeriesOption['label'],
    itemStyle: { color: series.color },
    ...symbol,
    ...(repeats !== undefined ? { symbolRepeat: repeats } : {}),
    symbolBoundingData: bounds,
    ...SLOTS,
    z: 3,
    emphasis: { focus: 'series' },
  };
};

// Faint icons behind each metric's bars, out to where its icons would reach
// at the chart's highest value: on the hidden copy of the category axis
// (TRACK_CATEGORY_AXIS_INDEX), in the same slots as the bars
export const toPictorialTrackOptions = ({
  model,
  pictorial,
  trackColor,
}: {
  model: StudioCharts.CartesianModel;
  pictorial: Studio.PictorialOptions;
  trackColor: string;
}): PictorialBarSeriesOption[] =>
  model.series
    .filter((s) => s.kind === 'bar')
    .map((series) => {
      const { bounds, repeats } = toBounds({
        model,
        axisIndex: series.axisIndex,
        unit: pictorial.mode === 'fill' ? undefined : pictorial.unit,
      });
      const [, high] = bounds;
      const [categoryAxis, valueAxis] = model.isHorizontal
        ? (['yAxisIndex', 'xAxisIndex'] as const)
        : (['xAxisIndex', 'yAxisIndex'] as const);
      return {
        type: 'pictorialBar',
        id: `${series.id}:track`,
        name: `${series.name} track`,
        [categoryAxis]: TRACK_CATEGORY_AXIS_INDEX,
        [valueAxis]: series.axisIndex,
        data: model.categories.map(() => high),
        itemStyle: { color: trackColor },
        ...toSymbol({
          pictorial,
          icon: getSeriesIcon({ pictorial, series }),
          isHorizontal: model.isHorizontal,
        }),
        ...(repeats !== undefined ? { symbolRepeat: repeats } : {}),
        // Drawn in full, not cut at a value
        symbolClip: false,
        symbolBoundingData: bounds,
        ...SLOTS,
        // Over the pointer's band, under the icons
        z: 2,
        silent: true,
        tooltip: { show: false },
        emphasis: { disabled: true },
      };
    });
